import React from 'react';
import { Material, Recipe, Monster, AnyContent, ContentTab } from '../types/adminContent';

interface Props {
  item: AnyContent;
  activeTab: ContentTab;
  getRarityColor: (rarity: string) => string;
  onClose: () => void;
}

const ContentDetailsModal: React.FC<Props> = ({ item, activeTab, getRarityColor, onClose }) => {
  const tabLabel = activeTab.charAt(0).toUpperCase() + activeTab.slice(0, -1);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📊 {tabLabel} Details</h3>
          <button onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="item-details">
            <div className="detail-section">
              <h4>Basic Information</h4>
              <div className="detail-grid">
                <div><strong>ID:</strong> {item.id}</div>
                <div><strong>Name:</strong> {item.name}</div>
                <div><strong>Status:</strong> {item.isActive ? '✅ Active' : '❌ Inactive'}</div>
                <div><strong>Created:</strong> {new Date(item.createdAt).toLocaleString()}</div>
                <div><strong>Updated:</strong> {new Date(item.updatedAt).toLocaleString()}</div>
              </div>
              <div className="description">
                <strong>Description:</strong>
                <p>{item.description}</p>
              </div>
            </div>

            {activeTab === 'materials' && 'rarity' in item && (
              <div className="detail-section">
                <h4>Material Properties</h4>
                <div className="detail-grid">
                  <div><strong>Type:</strong> {(item as Material).materialType}</div>
                  <div>
                    <strong>Rarity:</strong>{' '}
                    <span style={{ color: getRarityColor((item as Material).rarity) }}>
                      {(item as Material).rarity}
                    </span>
                  </div>
                  <div><strong>Base Value:</strong> 💰 {(item as Material).baseValue}</div>
                  <div><strong>Icon:</strong> {(item as Material).iconName}</div>
                </div>
              </div>
            )}

            {activeTab === 'recipes' && 'ingredients' in item && (
              <>
                <div className="detail-section">
                  <h4>Recipe Properties</h4>
                  <div className="detail-grid">
                    <div><strong>Category:</strong> {(item as Recipe).category}</div>
                    <div><strong>Required Level:</strong> 🎯 {(item as Recipe).requiredLevel}</div>
                    <div><strong>Crafting Time:</strong> ⏱️ {(item as Recipe).craftingTime}s</div>
                    <div><strong>Base Success Rate:</strong> ✅ {(item as Recipe).baseSuccessRate}%</div>
                  </div>
                </div>
                <div className="detail-section">
                  <h4>Ingredients ({(item as Recipe).ingredients.length})</h4>
                  <div className="ingredients-list">
                    {(item as Recipe).ingredients.map((ingredient, index) => (
                      <div key={index} className="ingredient-item">
                        <span className="ingredient-name">{ingredient.materialName}</span>
                        <span className="ingredient-quantity">×{ingredient.quantity}</span>
                        <span
                          className="ingredient-rarity"
                          style={{ color: getRarityColor(ingredient.rarity) }}
                        >
                          {ingredient.rarity}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'monsters' && 'level' in item && 'health' in item && (
              <>
                <div className="detail-section">
                  <h4>Monster Stats</h4>
                  <div className="detail-grid">
                    <div><strong>Level:</strong> 🎯 {(item as Monster).level}</div>
                    <div><strong>Type:</strong> {(item as Monster).monsterType}</div>
                    <div><strong>AI Personality:</strong> {(item as Monster).aiPersonality}</div>
                  </div>
                  <div className="stats-display">
                    <div className="stat-item"><span className="stat-icon">❤️</span><span>Health: {(item as Monster).health}</span></div>
                    <div className="stat-item"><span className="stat-icon">⚔️</span><span>Damage: {(item as Monster).damage}</span></div>
                    <div className="stat-item"><span className="stat-icon">🛡️</span><span>Defense: {(item as Monster).defense}</span></div>
                    <div className="stat-item"><span className="stat-icon">💨</span><span>Speed: {(item as Monster).speed}</span></div>
                  </div>
                </div>

                {(item as Monster).dropTable?.length > 0 && (
                  <div className="detail-section">
                    <h4>Drop Table ({(item as Monster).dropTable.length} items)</h4>
                    <div className="drop-table">
                      {(item as Monster).dropTable.map((drop, index) => (
                        <div key={index} className="drop-item">
                          <span className="drop-material">{drop.materialName}</span>
                          <span className="drop-rate">{drop.dropRate}%</span>
                          <span className="drop-quantity">{drop.minQuantity}-{drop.maxQuantity}x</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="admin-close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ContentDetailsModal;
